import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useResendDoubleOptInEmailMutation } from "src/api/client/resend-double-opt-in-email.api";
import { useResendRegistrationEmailMutation } from "src/api/client/resend-registration-email.api";
import { IconCheckCircle } from "src/components/icons-mui/check-circle";
import { IconCloseCircle } from "src/components/icons-mui/close-circle";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { WF_DIALOG_SPACE_VARIANTS, WfDialog } from "src/components-mui/common/dialog/wf-dialog";
import { WfDialogFooter } from "src/components-mui/common/dialog/wf-dialog-footer";
import { getGlobals } from "src/consts/globals";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { EnumUserAction } from "src/types/layout";
import { useIsomorphicLayoutEffect } from "src/utils/common-util";

interface IMessageWithIndicatorProps {
    isSuccess?: boolean;
    content?: string;
}

const MessageWithIndicator = ({ isSuccess, content }: IMessageWithIndicatorProps) => {
    return (
        <Box display="flex" gap={1} justifyContent="center" alignItems="center" fontSize="xl" flexWrap={["wrap", "nowrap"]}>
            {isSuccess ? (
                <IconCheckCircle
                    sx={{
                        color: "white",
                        fill: "green.600",
                        width: { xs: "32px", sm: "24px" },
                        height: { xs: "32px", sm: "24px" },
                    }}
                />
            ) : (
                <IconCloseCircle sx={{ color: "white", fill: "red.600", width: { xs: "40px", sm: "24px" }, height: { xs: "32px", sm: "24px" } }} />
            )}
            <Typography width={["100%", "auto"]} fontSize="xl">
                {content}
            </Typography>
        </Box>
    );
};

const USER_ACTION_URL_PARAM = "userAction";
const USER_ID_URL_PARAM = "userId";

export const ActivationModal = () => {
    const { accountActivationNewsletterOptInResult } = getGlobals() ?? {};
    const { userAction, isActionSuccessful, activationModalContent } = accountActivationNewsletterOptInResult ?? {};
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { t } = useTranslation("common");
    const toast = useWfToast();
    const router = useRouter();
    const shouldOpenActivationModal = Boolean(accountActivationNewsletterOptInResult && userAction);
    const resendRegistrationEmailMutation = useResendRegistrationEmailMutation();
    const resendDoubleOptInEmailMutation = useResendDoubleOptInEmailMutation();

    useIsomorphicLayoutEffect(() => {
        if (shouldOpenActivationModal) {
            setIsOpen(true);
        }
    }, [shouldOpenActivationModal]);

    const handleModalClose = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete(USER_ACTION_URL_PARAM);
        url.searchParams.delete(USER_ID_URL_PARAM);

        const newUrl = url.pathname + (url.search ? url.search : "");
        router.replace(newUrl, undefined, { shallow: true });

        setIsOpen(false);
    };

    const handleButtonClick = async () => {
        if (isActionSuccessful) {
            return handleModalClose();
        }

        try {
            if (userAction === EnumUserAction.AccountActivation) {
                await resendRegistrationEmailMutation.mutatePromise({});
            }

            if (userAction === EnumUserAction.OptIn) {
                await resendDoubleOptInEmailMutation.mutatePromise({});
            }

            toast({ status: EnumToastStatus.Success, title: t("confirmation-email-sent") });
        } catch {
            toast({ status: EnumToastStatus.Error, title: t("general-error") });
        }

        return handleModalClose();
    };

    const shouldShowButton = !(!isActionSuccessful && userAction === EnumUserAction.AccountActivationNewsletterOptIn);

    return (
        <WfDialog isOpen={isOpen} onClose={handleModalClose} space={WF_DIALOG_SPACE_VARIANTS.MOBILE_SPACIOUS_TOP}>
            <Box pb={[2, 3]} display="flex" justifyContent="center" alignItems="center">
                <WfHeading component="h2" fontSize={{ xs: "xl", sm: "2xl" }}>
                    {activationModalContent?.title}
                </WfHeading>
            </Box>
            <Box textAlign="center">
                <MessageWithIndicator isSuccess={isActionSuccessful} content={activationModalContent?.subtitle} />
                <Typography mt={2} fontSize="md">
                    {activationModalContent?.description}
                </Typography>
            </Box>
            {shouldShowButton && (
                <WfDialogFooter mt={5}>
                    <Button
                        fullWidth
                        size="large"
                        loading={resendRegistrationEmailMutation.isLoading || resendDoubleOptInEmailMutation.isLoading}
                        onClick={handleButtonClick}
                    >
                        {activationModalContent?.buttonLabel}
                    </Button>
                </WfDialogFooter>
            )}
        </WfDialog>
    );
};
